import { DislikeOutlined, LikeOutlined } from "@ant-design/icons";
import { ReloadOutlined } from "@ant-design/icons";
import type React from "react";
import { useId, useState } from "react";
import { type PrechartRefreshResult, prechartBaseUrl, requestPrechartRefresh, sendPrechartFeedback } from "../prechart";

// The pre-visit brief: what changed since the patient's last visit, composed
// before the clinician opens the chart (backend `prechart/`). Posted into the
// thread by the `pre_visit_brief` custom action and restored from thread
// metadata anchored to its lead-in message. Payload shape:
// docs/precharting/api-contract.md (BriefPayload).
//
// Read-only apart from three calls that never touch the chat stream: source
// chips expand their detail in place, Refresh queues a fresh pre-chart, and
// thumbs up/down store feedback on this brief version.
export interface BriefSource {
  // The fact id ("result:<id>", "doc:<id>", "enc:<id>", "outside:<key>",
  // "outside-proc:<key>"), as the harness's Source.ref.
  ref: string;
  kind?: "result" | "document" | "encounter" | "outside_result" | "outside_procedure" | string;
  label: string;
  unreviewed?: boolean;
  detail?: string | null;
}

export interface BriefItem {
  text: string;
  priority?: "high" | "normal" | string;
  sources?: BriefSource[];
}

export interface BriefSection {
  key: string;
  title: string;
  items?: BriefItem[];
}

export interface BriefCoverage {
  complete?: boolean;
  sources?: Array<{ source: string; status: string; count?: number; detail?: string }>;
  gaps?: string[];
}

export interface PreVisitBriefData {
  brief_id: string;
  run_id?: string;
  version?: number;
  status?: string;
  patient_name?: string;
  appointment?: { date?: string | null; time?: string | null; visit_type?: string | null } | null;
  anchor?: {
    date?: string | null;
    visit_type?: string | null;
    days_ago?: number | null;
    rule?: string | null;
  } | null;
  is_new_patient?: boolean;
  headline?: string | null;
  sections?: BriefSection[];
  coverage?: BriefCoverage | null;
  generated_at?: string;
  composer?: "llm" | "deterministic" | string;
}

interface PreVisitBriefProps {
  data: PreVisitBriefData;
  // The patient the panel is open on — Refresh queues a pre-chart for them.
  // Absent (no patient selected) ⇒ no Refresh.
  clientId?: number;
  // A newer brief is further down the thread: read-only, no actions.
  frozen?: boolean;
}

// "2026-09-30" as a local calendar date (never shifted by the timezone).
const parseDay = (iso?: string | null): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

// "Wed, Sep 30"
export const appointmentDayLabel = (iso?: string | null): string => {
  const d = parseDay(iso);
  return d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : iso || "";
};

// "Jul 5", with the year when it isn't the reference year ("Jul 5, 2025").
export const lastVisitDayLabel = (iso?: string | null, referenceYear?: number): string => {
  const d = parseDay(iso);
  if (!d) return iso || "";
  const year = referenceYear ?? new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== year ? { year: "numeric" } : {}),
  });
};

export const daysAgoLabel = (days?: number | null): string => {
  if (days == null || Number.isNaN(days)) return "";
  if (days <= 0) return "today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

const ClipboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
    <rect x="3" y="2.5" width="10" height="11.5" rx="1.5" stroke="#6366F1" strokeWidth="1.3" />
    <path d="M6 2.5h4v1.5H6z" fill="#EEF2FF" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5.5 7h5M5.5 9.5h5M5.5 12h3" stroke="#6366F1" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

// One source chip. A chip with detail is a toggle button; one without is a
// plain label (nothing to expand, so nothing to click).
const SourceChip: React.FC<{
  source: BriefSource;
  expanded: boolean;
  detailId: string;
  onToggle: () => void;
}> = ({ source, expanded, detailId, onToggle }) => {
  const hasDetail = !!source.detail?.trim();
  const content = (
    <>
      <span style={styles.chipLabel}>{source.label}</span>
      {source.unreviewed && <span style={styles.unreviewed}>Unreviewed</span>}
    </>
  );
  if (!hasDetail) return <span style={styles.chip}>{content}</span>;
  return (
    <button
      type="button"
      style={{ ...styles.chip, ...styles.chipButton, ...(expanded ? styles.chipExpanded : {}) }}
      aria-expanded={expanded}
      aria-controls={detailId}
      onClick={onToggle}
    >
      {content}
    </button>
  );
};

const MAX_CHIPS = 4;

const SourceChips: React.FC<{
  sources?: BriefSource[];
  keyPrefix: string;
  idPrefix: string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
}> = ({ sources, keyPrefix, idPrefix, expanded, onToggle }) => {
  const [showAll, setShowAll] = useState(false);
  const list = (sources ?? []).filter((s) => !!s?.label?.trim());
  if (!list.length) return null;
  // A backlog line can cite dozens of documents; keep the row to one glance.
  const shown = showAll ? list : list.slice(0, MAX_CHIPS);
  const open = shown
    .map((s, i) => ({ s, key: `${keyPrefix}:${i}` }))
    .filter(({ s, key }) => expanded.has(key) && !!s.detail?.trim());
  return (
    <>
      <div style={styles.chipRow}>
        {shown.map((s, i) => {
          const key = `${keyPrefix}:${i}`;
          return (
            <SourceChip
              key={key}
              source={s}
              expanded={expanded.has(key)}
              detailId={`${idPrefix}-${key}`}
              onToggle={() => onToggle(key)}
            />
          );
        })}
        {list.length > shown.length && (
          <button type="button" style={{ ...styles.chip, ...styles.chipButton }} onClick={() => setShowAll(true)}>
            +{list.length - shown.length} more
          </button>
        )}
      </div>
      {open.map(({ s, key }) => (
        <div key={key} id={`${idPrefix}-${key}`} style={styles.detail}>
          <span style={styles.detailLabel}>{s.label}:</span> {s.detail}
        </div>
      ))}
    </>
  );
};

type FeedbackState = { phase: "idle" | "sending" | "sent" | "error"; helpful?: boolean };

const PreVisitBrief: React.FC<PreVisitBriefProps> = ({ data, clientId, frozen }) => {
  const idPrefix = useId();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [refresh, setRefresh] = useState<"idle" | "sending" | PrechartRefreshResult>("idle");
  const [feedback, setFeedback] = useState<FeedbackState>({ phase: "idle" });

  if (!data?.brief_id) return null;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const appt = data.appointment;
  const apptLine = appt ? [appointmentDayLabel(appt.date), appt.time, appt.visit_type].filter(Boolean).join(" · ") : "";
  const referenceYear = parseDay(appt?.date)?.getFullYear();
  const lastVisitLine = data.is_new_patient
    ? "New patient"
    : data.anchor?.date
      ? [
          `Last visit ${lastVisitDayLabel(data.anchor.date, referenceYear)}`,
          data.anchor.visit_type,
          daysAgoLabel(data.anchor.days_ago),
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

  const sections = (data.sections ?? [])
    .map((s, index) => ({ ...s, index, items: (s.items ?? []).filter((it) => !!it?.text?.trim()) }))
    .filter((s) => s.items.length > 0);
  const coverageGaps = (data.coverage?.gaps ?? []).filter((g) => !!g?.trim());
  const incomplete = data.coverage?.complete === false;

  const onRefresh = async () => {
    if (!clientId || frozen || refresh === "sending") return;
    setRefresh("sending");
    setRefresh(await requestPrechartRefresh(prechartBaseUrl(), clientId));
  };

  const onFeedback = async (helpful: boolean) => {
    if (frozen || feedback.phase === "sending" || feedback.phase === "sent") return;
    setFeedback({ phase: "sending", helpful });
    const ok = await sendPrechartFeedback(prechartBaseUrl(), data.brief_id, helpful, clientId);
    setFeedback(ok ? { phase: "sent", helpful } : { phase: "error" });
  };

  // Only a failed request can be retried; queued / running / turned off stay put.
  const refreshLocked = refresh !== "idle" && refresh !== "error";
  const refreshLabel =
    refresh === "sending" ? "Refreshing…" : refresh === "queued" || refresh === "in_progress" ? "Queued" : "Refresh";
  const refreshMessage =
    refresh === "queued"
      ? "Refresh queued. The updated brief shows the next time you open this patient."
      : refresh === "in_progress"
        ? "A refresh is already running for this visit."
        : refresh === "disabled"
          ? "Pre-charting is turned off for you."
          : refresh === "error"
            ? "Couldn't queue a refresh. Try again."
            : "";
  const feedbackLocked = feedback.phase === "sending" || feedback.phase === "sent";
  const feedbackMessage =
    feedback.phase === "sent"
      ? "Thanks for the feedback."
      : feedback.phase === "error"
        ? "Couldn't send feedback. Try again."
        : "";

  return (
    <section style={{ ...styles.container, ...(frozen ? styles.frozenBox : {}) }} aria-label="Pre-visit brief">
      <div style={styles.header}>
        <div style={{ ...styles.iconBadge, ...(frozen ? styles.iconBadgeFrozen : {}) }}>
          <ClipboardIcon />
        </div>
        <div style={styles.headerText}>
          <span style={styles.title}>Pre-visit brief</span>
          {apptLine && <span style={styles.meta}>{apptLine}</span>}
          {lastVisitLine && <span style={styles.meta}>{lastVisitLine}</span>}
          {data.composer === "deterministic" && <span style={styles.plainNote}>Plain listing (AI unavailable)</span>}
        </div>
      </div>

      {frozen && <div style={styles.frozenCaption}>An updated brief is further down the thread.</div>}

      {data.headline?.trim() && <div style={styles.headline}>{data.headline}</div>}

      {sections.map((section) => {
        const ordered = [
          ...section.items.filter((it) => it.priority === "high"),
          ...section.items.filter((it) => it.priority !== "high"),
        ];
        return (
          <div key={`${section.key}-${section.index}`} style={styles.section}>
            <div style={styles.sectionTitle}>{section.title}</div>
            <ul style={styles.itemList}>
              {ordered.map((item, i) => {
                const high = item.priority === "high";
                return (
                  <li key={i} style={styles.item} data-priority={high ? "high" : "normal"}>
                    <span style={{ ...styles.bullet, ...(high ? styles.bulletHigh : {}) }} aria-hidden="true" />
                    <div style={styles.itemBody}>
                      <span style={{ ...styles.itemText, ...(high ? styles.itemTextHigh : {}) }}>
                        {high && <span style={styles.srOnly}>High priority: </span>}
                        {item.text}
                      </span>
                      <SourceChips
                        sources={item.sources}
                        keyPrefix={`s${section.index}:${i}`}
                        idPrefix={idPrefix}
                        expanded={expanded}
                        onToggle={toggle}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      {sections.length === 0 && <div style={styles.empty}>Nothing new found in the records read.</div>}

      {incomplete && (
        <div style={styles.coverage}>
          Coverage incomplete: {coverageGaps.length ? coverageGaps.join("; ") : "some sources could not be read"}.
        </div>
      )}

      {!frozen && (
        <div style={styles.actions}>
          {clientId ? (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshLocked}
              style={{ ...styles.ghostButton, ...(refreshLocked ? styles.buttonDisabled : {}) }}
            >
              <ReloadOutlined spin={refresh === "sending"} style={{ fontSize: 12 }} />
              {refreshLabel}
            </button>
          ) : (
            <span />
          )}
          <div style={styles.feedback}>
            <button
              type="button"
              aria-label="Helpful"
              title="Helpful"
              aria-pressed={feedback.helpful === true && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(true)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === true && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== true ? styles.buttonDisabled : {}),
              }}
            >
              <LikeOutlined aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Not helpful"
              title="Not helpful"
              aria-pressed={feedback.helpful === false && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(false)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === false && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== false ? styles.buttonDisabled : {}),
              }}
            >
              <DislikeOutlined aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {!frozen && (refreshMessage || feedbackMessage) && (
        <div style={styles.status} role="status">
          {[refreshMessage, feedbackMessage].filter(Boolean).join(" ")}
        </div>
      )}
    </section>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    marginTop: "8px",
    borderRadius: "14px",
    border: "1px solid #E2E4F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  frozenBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", boxShadow: "none" },
  frozenCaption: { fontSize: "12px", fontWeight: 500, color: "#9CA3AF", lineHeight: 1.4 },
  header: { display: "flex", alignItems: "flex-start", gap: "9px" },
  iconBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    backgroundColor: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  iconBadgeFrozen: { backgroundColor: "#F3F4F6", filter: "grayscale(1)" },
  headerText: { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0, paddingTop: "3px" },
  title: { fontSize: "13.5px", fontWeight: 600, color: "#111827", lineHeight: 1.45 },
  meta: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
  plainNote: { fontSize: "11.5px", fontStyle: "italic", color: "#9CA3AF", lineHeight: 1.4 },
  headline: { fontSize: "13px", fontWeight: 500, color: "#1F2937", lineHeight: 1.5 },
  section: { display: "flex", flexDirection: "column", gap: "6px" },
  sectionTitle: {
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#6B7280",
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  itemList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "8px" },
  item: { display: "flex", alignItems: "flex-start", gap: "8px" },
  bullet: {
    width: "6px",
    height: "6px",
    borderRadius: "999px",
    backgroundColor: "#D1D5DB",
    flexShrink: 0,
    marginTop: "7px",
  },
  bulletHigh: { width: "7px", height: "7px", backgroundColor: "#DC2626", marginTop: "6px" },
  itemBody: { display: "flex", flexDirection: "column", gap: "4px", minWidth: 0, flex: 1 },
  itemText: { fontSize: "13px", color: "#374151", lineHeight: 1.45 },
  itemTextHigh: { fontWeight: 600, color: "#111827" },
  srOnly: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  chipRow: { display: "flex", flexWrap: "wrap", gap: "4px" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    maxWidth: "100%",
    padding: "2px 8px",
    borderRadius: "999px",
    // Longhands: the expanded state swaps only the colour.
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#E0E7FF",
    backgroundColor: "#F5F7FF",
    fontSize: "11.5px",
    fontWeight: 500,
    color: "#4338CA",
    lineHeight: 1.5,
    fontFamily: "inherit",
  },
  chipButton: { cursor: "pointer" },
  chipExpanded: { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  chipLabel: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  unreviewed: {
    fontSize: "10.5px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    color: "#B45309",
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
  },
  detail: {
    fontSize: "12px",
    color: "#4B5563",
    lineHeight: 1.45,
    padding: "6px 9px",
    borderRadius: "8px",
    backgroundColor: "#F9FAFB",
    borderLeft: "2px solid #C7D2FE",
    whiteSpace: "pre-wrap",
  },
  detailLabel: { fontWeight: 600, color: "#374151" },
  empty: { fontSize: "12.5px", color: "#6B7280", lineHeight: 1.45 },
  coverage: { fontSize: "11.5px", color: "#9CA3AF", lineHeight: 1.45 },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    borderTop: "1px solid #F0F1F5",
    paddingTop: "8px",
  },
  ghostButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    fontSize: "12.5px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid #D6D9E0",
    backgroundColor: "#FFFFFF",
    color: "#374151",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
  },
  feedback: { display: "flex", gap: "4px" },
  thumb: {
    width: "30px",
    height: "28px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    borderRadius: "8px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "transparent",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1,
  },
  thumbPicked: { borderColor: "#C7D2FE", backgroundColor: "#EEF2FF", cursor: "default" },
  buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },
  status: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
};

export default PreVisitBrief;
